import { FetchingButton } from '@glade-ai/ui';
import React, { type FC, type ReactElement, useEffect, useRef } from 'react';

import ProgressIndicator from '@/components/ProgressIndicator';
import isInIframe from '@/helpers/isInIframe';

type Props = {
  loadFn: () => void;
  isDisabled: boolean;
  intersectTime?: number;
  showSpinner?: boolean;
  rootEl?: Element | null;
};

const PaginationObserver: FC<Props> = ({
  showSpinner = true,
  loadFn,
  isDisabled,
  intersectTime,
  rootEl,
}): ReactElement => {
  const boxElement = useRef<HTMLDivElement>(null);
  const loadFnRef = useRef<Props['loadFn']>(loadFn);
  const loadFnTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    loadFnRef.current = loadFn;
  }, [loadFn]);

  useEffect(() => {
    if (!isInIframe()) {
      const handleIntersect: ConstructorParameters<typeof IntersectionObserver>[0] = (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (intersectTime) {
              loadFnTimeoutRef.current = setTimeout(() => loadFnRef.current(), intersectTime);
            } else {
              loadFnRef.current();
            }
          } else if (loadFnTimeoutRef.current) {
            clearTimeout(loadFnTimeoutRef.current);
            loadFnTimeoutRef.current = null;
          }
        });
      };

      const options = {
        root: rootEl || null,
        rootMargin: '0px',
        threshold: [0.75],
      };

      const boxRef = boxElement.current;
      const observer = new IntersectionObserver(handleIntersect, options);
      if (boxRef) {
        observer.observe(boxRef);
      }

      return () => {
        if (boxRef) {
          observer.unobserve(boxRef);
        }

        if (loadFnTimeoutRef.current) {
          clearTimeout(loadFnTimeoutRef.current);
          loadFnTimeoutRef.current = null;
        }
      };
    }

    return () => {};
  }, [boxElement, intersectTime, rootEl]);

  return (
    <>
      {isInIframe() && !isDisabled && (
        <>
          <FetchingButton
            size='sm'
            disabled={isDisabled}
            fetching={isDisabled}
            onClick={() => loadFnRef?.current?.()}
            className='my-4 mx-2'
          >
            Load more...
          </FetchingButton>
        </>
      )}
      {!isInIframe() && (
        <div
          ref={boxElement}
          style={{
            alignItems: 'center',
            display: 'flex',
            height: showSpinner ? '56px' : '1px',
            justifyContent: 'center',
            pointerEvents: 'none',
            width: '100%',
          }}
        >
          {showSpinner && <ProgressIndicator isCentered />}
        </div>
      )}
    </>
  );
};

export default PaginationObserver;
